import React, { useState, useContext, useMemo } from 'react'
import { View, ScrollView } from 'react-native'
import { Text, Button, Spinner, Radio } from '@ui-kitten/components'
import StyledTextInput from './StyledTextInput'
import PlatformDependantSelect from './PlatformDependantSelect'
import { addNewClient } from '../global/Firebase/firebase'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { discountOptionsListAtom, userRoleAtom, priceTypesAtom } from '../global/AtomsProvider'
import { getDailyStatID } from '../utils/timezoneFunctions'

export const CreateClientView = React.memo(function CreateClientView({db, defaultBonusLevel, store, storeInfo, organizationId, onCreate}) {
  const { t } = useTranslation()
  const originalDiscountOptions = useAtomValue(discountOptionsListAtom)
  const discountOptions = useMemo(() => {
    return originalDiscountOptions.filter(option => option.percentage != null)
  }, [originalDiscountOptions])
  const userRole = useAtomValue(userRoleAtom)
  const priceTypes = useAtomValue(priceTypesAtom)
  const [phoneValue, setPhoneValue] = useState('')
  const [nameValue, setNameValue] = useState('')
  const [addressValue, setAddressValue] = useState('')
  const [clientType, setClientType] = useState('b2c')
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(null)
  const [preferredPriceType, setPreferredPriceType] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  return (
    <ScrollView style={{marginVertical: 10}}>
      <Text category='h5' style={{marginBottom: 10}}>{t('clientView.newClient')}</Text>
      <Radio style={{marginBottom: 5}} testID='client-type-b2c' checked={clientType === 'b2c'} onChange={() => {setClientType('b2c')}}>
        {t('clientView.b2cClient')}
      </Radio>
      <Radio testID='client-type-b2b' checked={clientType === 'b2b'} onChange={() => {setClientType('b2b')}}>
        {t('clientView.b2bClient')}
      </Radio>

      <Text category='label' style={{marginBottom: 5, marginTop: 10}}>{t('name')}</Text>
      <StyledTextInput
        testID='client-name-input'
        value={nameValue}
        autoCapitalize='words'
        onChangeText={text => {
          setNameValue(text)
        }}
        returnKeyType='done'
        selectTextOnFocus={true}
      />

      <Text category='label' style={{marginBottom: 5, marginTop: 10}}>{t('phone')}</Text>
      <StyledTextInput
        testID='client-phone-input'
        value={phoneValue}
        keyboardType='phone-pad'
        onChangeText={text => {
          setPhoneValue(text)
        }}
        placeholder='+xxxxxxxxxxxx'
        returnKeyType='done'
        selectTextOnFocus={true}
      />

      <Text category='label' style={{marginBottom: 5, marginTop: 10}}>{t('clients.address')}</Text>
      <StyledTextInput
        testID='client-address-input'
        value={addressValue}
        autoCapitalize='words'
        onChangeText={text => {
          setAddressValue(text)
        }}
        returnKeyType='done'
        selectTextOnFocus={true}
      />
      
      { (userRole?.permissions?.['applyDiscount'] || userRole?.id === 'admin') && (
        <>
          <Text category='label' style={{marginBottom: 5, marginTop: 10}}>
            {t('clients.loyaltyDiscount')}
          </Text>
          <PlatformDependantSelect
            data={discountOptions}
            selectedOption={loyaltyDiscount}
            onSelect={(itemValue, itemIndex) => {
              setLoyaltyDiscount(itemValue)
            }}
            style={{marginBottom: 10}}
          />
        </>
      )}
      
      {priceTypes && priceTypes.length > 0 && (
        <>
          <Text category='label' style={{marginBottom: 5, marginTop: 10}}>
            {t('clients.preferredPriceType') || 'Preferred Price Type'}
          </Text>
          <PlatformDependantSelect
            data={[
              { id: null, name: t('clients.none') || 'None', text: t('clients.none') || 'None' },
              ...priceTypes.map(pt => ({ id: pt.id, name: pt.name, text: pt.name }))
            ]}
            selectedOption={preferredPriceType ? 
              { ...priceTypes.find(pt => pt.id === preferredPriceType), text: priceTypes.find(pt => pt.id === preferredPriceType)?.name } : 
              { id: null, name: t('clients.none') || 'None', text: t('clients.none') || 'None' }
            }
            onSelect={(itemValue) => {
              setPreferredPriceType(itemValue.id)
            }}
            style={{marginBottom: 10}}
          />
        </>
      )}
      
      { error && <Text status='danger' style={{marginVertical: 5}}>{error}</Text>}
      {isLoading ? (
          <View style={{marginTop: 15, alignItems: 'center'}}><Spinner /></View>
        ) : (
        <Button onPress={async () => {
          if (!phoneValue.startsWith('+')) {
            setError(t('clientView.errorForPhoneNumberFormat'))
          } else if (phoneValue.length < 9) {
            setError(t('clientView.errorForPhoneNumberLength'))
          } else {
            setError(null)
            setIsLoading(true)
            var newClient = {
              displayName: nameValue,
              phoneNumber: phoneValue,
              address: addressValue,
              clientType,
              bonusValue: 0, 
              bonusLevel: (defaultBonusLevel || 3),
              loyaltyDiscount: loyaltyDiscount,
              preferredPriceType: preferredPriceType,
              punchCardValue: 0,
              totalPurchasedValue: 0,
              registrationDate: new Date(),
              dailyStatID: getDailyStatID(store, new Date()),
              registrationSource: store,
              organizationId: organizationId,
              exists: true,
            }
            const newClientRef = await addNewClient(db, newClient)
            newClient['ref'] = newClientRef
            newClient['id'] = newClientRef.id
            onCreate(newClient)
            setIsLoading(false)
          }
        }} style={{marginTop: 20}} testID='client-create-submit'>
          {t('create')}
        </Button>
      )}       
    </ScrollView>
  )

}, function areEqual(prevProps, nextProps) {
  /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */
  if (prevProps.defaultBonusLevel !== nextProps.defaultBonusLevel) {
    return false
  }
  return true
})